import {inject, Injectable} from '@angular/core';
import {logEvent} from 'firebase/analytics';
import {FirebaseInitService} from './firebase-init.service';
import {AnalyticsEvent, AnalyticsEventName, AnalyticsStandardParam} from './analytics-events';

/**
 * Lightweight analytics service for key conversion and engagement events.
 * Logs to Firebase Analytics when available (browser + consent); no-op in SSR or when analytics is disabled.
 */
@Injectable({
	providedIn: 'root'
})
export class AnalyticsService {
	private readonly firebaseInit = inject(FirebaseInitService);

	/**
	 * Track an event. Uses Firebase Analytics when available; safe to call from any context (SSR, no consent).
	 * @param eventName - Event name (e.g. 'registration_complete', 'test_made')
	 * @param params - Optional params (values should be string or number for Firebase Analytics)
	 */
	trackEvent(eventName: string, params?: Record<string, unknown>): void {
		const analytics = this.firebaseInit.analytics;
		if (!analytics) {
			return;
		}
		try {
			const safeParams = params ? this.toAnalyticsParams(params) : undefined;
			if (safeParams && Object.keys(safeParams).length > 0) {
				logEvent(analytics, eventName, safeParams);
			} else {
				logEvent(analytics, eventName);
			}
		} catch {
			// Guard against missing analytics or SSR
		}
	}

	trackFunnelStep(step: 'landing' | 'registration' | 'profile' | 'test' | 'first_interaction', params?: Record<string, unknown>): void {
		const eventByStep: Record<string, AnalyticsEvent> = {
			landing: AnalyticsEventName.landingCtaClicked,
			registration: AnalyticsEventName.registrationCompleted,
			profile: AnalyticsEventName.profileCompleted,
			test: AnalyticsEventName.testCompleted,
			first_interaction: AnalyticsEventName.messageStarted
		};
		this.trackEvent(eventByStep[step], {
			[AnalyticsStandardParam.funnelStep]: step,
			...params
		});
	}

	/** Firebase Analytics accepts only string and number values. */
	private toAnalyticsParams(params: Record<string, unknown>): Record<string, string | number> {
		const out: Record<string, string | number> = {};
		for (const [key, value] of Object.entries(params)) {
			if (value === null || value === undefined) {
				continue;
			}
			if (typeof value === 'string' || typeof value === 'number') {
				out[key] = value;
			} else {
				out[key] = String(value);
			}
		}
		return out;
	}
}
